import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PhoneCall, Copy } from "lucide-react";

const FIELDS = [
  ["api_key", "API Key", "Used as the CallerDesk authcode on every call"],
  ["secret_key", "Secret Key", ""],
  ["integration_key", "Integration Key", ""],
];
const inputCls = "w-full h-10 border border-[#E6E4DD] rounded-sm px-3 text-sm text-forest focus:outline-none focus:border-forest";

export const CallerDeskSettings = () => {
  const [saved, setSaved] = useState(null);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ api_key: "", secret_key: "", integration_key: "", virtual_number: "" });
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const [s, st] = await Promise.all([api.get("/callerdesk/settings"), api.get("/callerdesk/status")]);
    setSaved(s.data); setStatus(st.data); setForm((f) => ({ ...f, virtual_number: s.data.virtual_number || "" }));
  };
  useEffect(() => { load().catch(() => {}); }, []);
  const save = async () => {
    const body = Object.fromEntries(Object.entries(form).filter(([k, v]) => v.trim() || (k === "virtual_number" && saved?.virtual_number)));
    setBusy(true);
    try { await api.put("/callerdesk/settings", body); toast.success("CallerDesk settings saved for this organisation"); setForm((f) => ({ ...f, api_key: "", secret_key: "", integration_key: "" })); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!saved) return null;
  return (
    <section className="border border-[#E6E4DD] bg-white rounded-sm p-6 space-y-4" data-testid="settings-callerdesk">
      <div className="flex items-start gap-3">
        <PhoneCall className="h-4 w-4 text-forest/60 mt-1" />
        <div className="flex-1">
          <div className="font-display font-bold text-xl text-forest tracking-tight">CallerDesk calling</div>
          <div className="text-xs text-forest/60 mt-1">These keys belong to this organisation only. All lead calls and Business Campaign dialling use them.</div>
        </div>
        <span data-testid="cd-settings-status" className={`text-[10px] uppercase tracking-[0.15em] font-bold ${saved.configured ? "text-[#2D6A4F]" : "text-clay"}`}>{saved.configured ? "Connected" : "Not set up"}</span>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {FIELDS.map(([k, label, hint]) => (
          <label key={k} className="text-xs text-forest/60">{label}
            <input data-testid={`cd-settings-${k}`} type="password" autoComplete="off" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              placeholder={saved[k] ? `Saved ${saved[k]} — type to replace` : `Enter ${label}`} className={`${inputCls} mt-1`} />
            {hint && <span className="text-[10px] text-forest/45">{hint}</span>}
          </label>
        ))}
        <label className="text-xs text-forest/60">Virtual number (DID)
          <input data-testid="cd-settings-virtual_number" value={form.virtual_number} onChange={(e) => setForm({ ...form, virtual_number: e.target.value })} placeholder="e.g. 08069XXXXXX" className={`${inputCls} mt-1`} />
          <span className="text-[10px] text-forest/45">The CallerDesk number calls ring out from</span>
        </label>
      </div>
      {status?.webhook_url && (
        <div>
          <div className="label-caps mb-1">Webhook URL — paste in this organisation's CallerDesk account (call reports & recordings)</div>
          <div className="flex items-center gap-2">
            <code data-testid="cd-settings-webhook" className="flex-1 text-[11px] bg-bone-alt/60 p-2 rounded-sm break-all">{status.webhook_url}</code>
            <button data-testid="cd-settings-webhook-copy" onClick={() => { navigator.clipboard.writeText(status.webhook_url); toast.success("Webhook URL copied"); }} className="h-9 w-9 grid place-items-center border border-[#E6E4DD] rounded-sm hover:border-forest"><Copy className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      )}
      <div className="flex justify-end">
        <button data-testid="cd-settings-save" onClick={save} disabled={busy} className="h-10 px-5 rounded-sm bg-forest text-white text-sm font-medium hover:bg-forest-soft disabled:opacity-60 transition-colors duration-150">{busy ? "Saving…" : "Save CallerDesk settings"}</button>
      </div>
    </section>
  );
};
